<section class="flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-4 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-orange-50 text-orange-600">
      <app-icon>upload_file</app-icon>
    </div>
    <h2 class="m-0 min-w-0 flex-1 truncate text-base font-bold leading-6">{{ 'stock.import.title' | translate }}</h2>
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
      [attr.aria-label]="'close' | translate"
      (click)="requestClose()"
    >
      <app-icon>close</app-icon>
    </button>
  </header>

  <div class="crm-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
    <p class="m-0 text-sm leading-5 text-slate-600">{{ 'stock.import.description' | translate }}</p>

    <app-table-v2 layout="embedded" size="sm" [columns]="tableColumns" [rows]="columns">
      <ng-template appTableV2Cell="name" [appTableV2CellOf]="columns" let-column>
        {{ column.name }}
        @if (column.required) {
          <span class="text-rose-500">*</span>
        }
      </ng-template>
      <ng-template appTableV2Cell="meaning" [appTableV2CellOf]="columns" let-column>
        {{ column.meaning | translate }}
      </ng-template>
    </app-table-v2>

    <ul class="m-0 flex list-disc flex-col gap-1 !pl-5 text-xs leading-5 text-slate-500">
      <li>{{ 'stock.import.rule_variants' | translate }}</li>
      <li>{{ 'stock.import.rule_dictionaries' | translate }}</li>
      <li>{{ 'stock.import.rule_errors' | translate }}</li>
      <li>{{ 'stock.import.rule_format' | translate }}</li>
    </ul>

    <label
      class="!flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 !border-dashed px-4 py-8 text-center transition"
      [ngClass]="
        dragOver
          ? '!border-orange-400 bg-orange-50'
          : '!border-slate-200 hover:!border-orange-300 hover:bg-orange-50/50'
      "
      (dragover)="$event.preventDefault(); dragOver = true"
      (dragleave)="dragOver = false"
      (drop)="$event.preventDefault(); dragOver = false; setFile($event.dataTransfer?.files?.[0])"
    >
      <input
        #fileInput
        type="file"
        class="hidden"
        accept=".csv,.xlsx"
        (change)="setFile(fileInput.files?.[0]); fileInput.value = ''"
      />
      <app-icon class="!h-8 !w-8 !text-[32px] !leading-none text-slate-400">cloud_upload</app-icon>
      <span class="text-sm font-medium text-slate-700">{{ 'stock.import.pick_file' | translate }}</span>
      <span class="text-xs text-slate-400">CSV, XLSX · 10 MB · 5000</span>
    </label>

    @if (file) {
      <div
        class="flex items-center gap-3 rounded-lg border px-3 py-2"
        [ngClass]="fileError ? '!border-red-200 bg-red-50' : '!border-slate-200 bg-slate-50'"
      >
        <app-icon class="!h-5 !w-5 !text-[20px] !leading-none text-slate-400">description</app-icon>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium text-slate-800">{{ file.name }}</div>
          @if (fileError) {
            <div class="text-xs text-red-600">{{ fileError | translate }}</div>
          } @else {
            <div class="text-xs text-slate-400">{{ file.size / 1024 | number: '1.0-0' }} KB</div>
          }
        </div>
        <button
          type="button"
          class="grid h-8 w-8 place-items-center rounded text-slate-400 hover:bg-white hover:text-rose-600"
          [attr.aria-label]="'delete' | translate"
          [disabled]="importing"
          (click)="setFile(null)"
        >
          <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">close</app-icon>
        </button>
      </div>
    }

    @if (result) {
      <div class="flex flex-col gap-3 rounded-lg border !border-slate-200 p-3">
        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-lg bg-emerald-50 px-3 py-2">
            <div class="text-xs text-emerald-700">{{ 'stock.import.products_created' | translate }}</div>
            <div class="text-xl font-bold text-emerald-700">{{ result.products_created }}</div>
          </div>
          <div class="rounded-lg bg-sky-50 px-3 py-2">
            <div class="text-xs text-sky-700">{{ 'stock.import.variants_created' | translate }}</div>
            <div class="text-xl font-bold text-sky-700">{{ result.variants_created }}</div>
          </div>
        </div>
        @if (result.errors.length) {
          <div class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-rose-600">
              {{ 'stock.import.errors_title' | translate: { count: result.errors.length } }}
            </span>
            <div class="crm-scroll max-h-64 overflow-y-auto rounded-lg border !border-rose-100">
              @for (error of result.errors; track $index) {
                <div class="flex gap-3 border-t !border-rose-50 px-3 py-1.5 text-xs first:border-t-0">
                  <span class="shrink-0 font-semibold text-slate-500">
                    {{ 'stock.import.row' | translate: { row: error.row } }}
                  </span>
                  <span class="min-w-0 break-words text-slate-700">{{ error.message }}</span>
                </div>
              }
            </div>
          </div>
        } @else {
          <span class="text-sm text-emerald-700">{{ 'stock.import.no_errors' | translate }}</span>
        }
      </div>
    }
  </div>

  <footer class="flex shrink-0 gap-2 border-t !border-slate-100 px-4 py-3">
    <app-button
      btnType="stroked"
      class="flex-1"
      [customClass]="'w-full !h-10 !rounded-lg'"
      (onChangeClick)="requestClose()"
    >
      {{ 'close' | translate }}
    </app-button>
    <app-button
      class="flex-1"
      [customClass]="'w-full !h-10 !rounded-lg'"
      [disabled]="!file || !!fileError || importing"
      (onChangeClick)="startImport()"
    >
      {{ (importing ? 'stock.import.importing' : 'stock.import.start') | translate }}
    </app-button>
  </footer>
</section>
